import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useHomepageRedesignEnabled } from '../../BonusFeed/hooks/useHomepageRedesignEnabled';
import { Person } from '../types';
import { useOneOnOnesAccess } from './use-one-on-ones-access';

interface Input {
	people: Person[];
	type: 'any' | 'feedback' | 'recognition';
}

export const useMonthlyInteraction = ({ people, type }: Input) => {
	const [noInteractionPeopleInMonth, interactionPeopleInMonth] = useMemo(() => {
		const noInteraction: Person[] = [];
		const interaction: Person[] = [];

		people.forEach((person) => {
			let interactionCount = 0;
			const feedback_count = person.feedback_given_count + person.feedback_requested_count;

			if (type === 'any') {
				interactionCount = person.recognition_count + feedback_count;
			} else if (type === 'feedback') {
				interactionCount = feedback_count;
			} else if (type === 'recognition') {
				interactionCount = person.recognition_count;
			}

			if (interactionCount > 0) {
				interaction.push(person);
			} else {
				noInteraction.push(person);
			}
		});
		return [noInteraction, interaction];
	}, [people, type]);

	return {
		noInteractionPeopleInMonth,
		interactionPeopleInMonth,
	};
};

export const useDaysSinceLastInteraction = (interactionDate?: number | null) => {
	return interactionDate ? Math.round((new Date().getTime() - new Date(interactionDate * 1000).getTime()) / 86400000) : -1;
};

export const useLastInteractedDate = (person: Person) => {
	const { canAccessOneOnOnes } = useOneOnOnesAccess();
	const homepageRedesignEnabled = useHomepageRedesignEnabled();
	const showFeedbackFeatures = canAccessOneOnOnes && !homepageRedesignEnabled;

	// NOTE: non-achieve customers should use recognition date
	if (!showFeedbackFeatures) {
		return person.last_recognized_at;
	}

	// NOTE: achieve customers should use the most recent interaction date between feedback and recognition
	if (!person.last_feedback_at && !person.last_recognized_at) {
		return null;
	}

	if (!person.last_feedback_at && person.last_recognized_at) {
		return person.last_recognized_at;
	}

	if (person.last_feedback_at && !person.last_recognized_at) {
		return person.last_feedback_at;
	}

	if (person.last_feedback_at && person.last_recognized_at) {
		const feedbackIsMoreRecent = person.last_feedback_at > person.last_recognized_at;

		if (feedbackIsMoreRecent) {
			return person.last_feedback_at;
		}
		return person.last_recognized_at;
	}
};

export const useInteractionTooltip = (person: Person) => {
	const { t } = useTranslation('tracker_widget');
	const { canAccessOneOnOnes } = useOneOnOnesAccess();
	const homepageRedesignEnabled = useHomepageRedesignEnabled();
	const showFeedbackFeatures = canAccessOneOnOnes && !homepageRedesignEnabled;

	const interactions = [];
	if (person.recognition_count > 0) {
		interactions.push(
			t('person_list.recognition_count_tooltip', {
				count: person.recognition_count,
			})
		);
	}
	if (showFeedbackFeatures && person.feedback_given_count > 0) {
		interactions.push(
			t('person_list.feedback_given_count_tooltip', {
				count: person.feedback_given_count,
			})
		);
	}
	if (showFeedbackFeatures && person.feedback_requested_count > 0) {
		interactions.push(
			t('person_list.feedback_requested_count_tooltip', {
				count: person.feedback_requested_count,
			})
		);
	}

	return interactions.join(', ');
};
